
import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import LoginForm from '@/components/LoginForm';
import RegisterForm from '@/components/RegisterForm';
import { useAuth } from '@/hooks/useAuth';
import { isAdmin } from '@/utils/roleValidation';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const Auth = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState('login');

  // Check for custom return action first
  const returnAction = typeof window !== 'undefined' ? localStorage.getItem('returnToAction') : null;
  
  // Check for redirect from sessionStorage (for reschedule approval, etc.)
  const redirectAfterLogin = typeof window !== 'undefined' ? sessionStorage.getItem('redirectAfterLogin') : null;
  
  // Get the intended destination from location state or URL params
  // Redirect admins to dashboard instead of profile
  const getDefaultRedirect = () => {
    if (user && isAdmin(user)) {
      return '/dashboard';
    }
    return location.state?.from?.pathname || '/profile';
  };
  
  const from = returnAction === 'create-success-story' ? '/' : getDefaultRedirect();
  const defaultTab = location.state?.defaultTab || 'login';
  
  // Check for returnTo parameter in URL params (for specific chat subjects)
  const urlParams = new URLSearchParams(location.search);
  const returnTo = urlParams.get('returnTo');
  const redirect = urlParams.get('redirect');
  const tutorId = urlParams.get('tutor');
  const mode = urlParams.get('mode');
  const inviteToken = urlParams.get('invite'); // Group booking invitation token

  // Set the default tab based on navigation state or URL mode parameter
  useEffect(() => {
    if (mode) {
      setCurrentTab(mode === 'register' ? 'register' : 'login');
    } else if (inviteToken) {
      // If there's an invite token, default to register tab
      setCurrentTab('register');
    } else {
      setCurrentTab(defaultTab);
    }
  }, [defaultTab, mode, inviteToken]);

  // If user is already authenticated, redirect to intended destination
  if (user) {
    console.log('🔐 User authenticated, checking redirects:', {
      redirectAfterLogin,
      redirect,
      returnTo
    });
    
    // Clear the redirect from sessionStorage after use
    if (redirectAfterLogin) {
      console.log('↪️ Redirecting to stored path:', redirectAfterLogin);
      sessionStorage.removeItem('redirectAfterLogin');
      return <Navigate to={redirectAfterLogin} replace />;
    }
    if (redirect) {
      console.log('↪️ Redirecting to redirect param:', redirect);
      // Use the redirect parameter (higher priority)
      return <Navigate to={redirect} replace />;
    }
    if (returnTo) {
      console.log('↪️ Redirecting to returnTo:', returnTo);
      // Decode the return URL (which may include query parameters like ?subject=programming)
      const redirectUrl = decodeURIComponent(returnTo);
      const finalUrl = tutorId ? `${redirectUrl}&tutor=${tutorId}` : redirectUrl;
      return <Navigate to={finalUrl} replace />;
    }
    return <Navigate to={from} replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col flex-1">
      <Navbar />
      
      <div className="flex-1 flex items-center justify-center py-16 lg:py-28 xl:py-32 px-4 sm:px-6 lg:px-8">
        <div className="w-full space-y-8 flex-1">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Welcome to Tutor<span className="text-brand-violet">Nesta</span>
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Join us as a student or tutor
            </p>
            <p className="mt-1 text-sm text-gray-600">
              انضم إلينا كطالب أو مدرس
            </p>
          </div>

          <Card className="mt-8 max-w-md mx-auto">
            <CardHeader className="text-center">
              <CardTitle>Get Started</CardTitle>
              <CardDescription>
                Sign in to your account or create a new one
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={currentTab} onValueChange={setCurrentTab}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login">Sign In</TabsTrigger>
                  <TabsTrigger value="register">Sign Up</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <LoginForm 
                    redirectTo={redirect || (returnTo ? decodeURIComponent(returnTo) : from)} 
                    inviteToken={inviteToken || undefined}
                  />
                </TabsContent>
                
                <TabsContent value="register">
                  <RegisterForm 
                    onToggleMode={() => setCurrentTab('login')} 
                    inviteToken={inviteToken || undefined}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Auth;
